import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendingUp } from "lucide-react";
import { usePirate, LEVELS, formatBerry } from "../lib/PirateContext";

const HASH = "#gerarchia";
const OPEN_EVENT = "crewhierarchy:open";

/**
 * Programmatically open the Crew Hierarchy bottom sheet.
 * Uses history.pushState so the native Back button closes it.
 */
export function openCrewHierarchy() {
    if (typeof window === "undefined") return;
    if (window.location.hash === HASH) return;
    window.history.pushState(
        { crewHierarchy: true },
        "",
        window.location.pathname + window.location.search + HASH
    );
    window.dispatchEvent(new Event(OPEN_EVENT));
}

export default function CrewHierarchySheet() {
    const { level, xp, berry, nextLevel, progress } = usePirate();
    const [open, setOpen] = useState(false);

    // Sync sheet state with URL hash / history
    useEffect(() => {
        const sync = () => setOpen(window.location.hash === HASH);
        sync();
        window.addEventListener("popstate", sync);
        window.addEventListener("hashchange", sync);
        window.addEventListener(OPEN_EVENT, sync);
        return () => {
            window.removeEventListener("popstate", sync);
            window.removeEventListener("hashchange", sync);
            window.removeEventListener(OPEN_EVENT, sync);
        };
    }, []);

    const close = useCallback(() => {
        if (window.location.hash !== HASH) {
            setOpen(false);
            return;
        }
        if (window.history.state && window.history.state.crewHierarchy) {
            // Native back — restores previous URL, no reload
            window.history.back();
        } else {
            // Fallback: replace state to strip the hash without navigation
            window.history.replaceState(
                null,
                "",
                window.location.pathname + window.location.search
            );
            setOpen(false);
        }
    }, []);

    // Prevent body scroll while open
    useEffect(() => {
        if (open) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prev;
            };
        }
    }, [open]);

    return (
        <AnimatePresence>
            {open && (
                <div
                    className="fixed inset-0 z-[70]"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Gerarchia della Ciurma"
                    data-testid="crew-hierarchy-sheet"
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={close}
                        className="absolute inset-0 bg-ink/70"
                        data-testid="crew-hierarchy-overlay"
                    />
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{
                            type: "spring",
                            damping: 28,
                            stiffness: 260,
                        }}
                        drag="y"
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={{ top: 0, bottom: 0.6 }}
                        dragMomentum={false}
                        onDragEnd={(_, info) => {
                            if (
                                info.offset.y > 120 ||
                                info.velocity.y > 500
                            ) {
                                close();
                            }
                        }}
                        className="absolute left-0 right-0 bottom-0 mx-auto md:max-w-xl bg-parchment border-t-4 border-x-2 md:border-2 border-ink shadow-manga-lg max-h-[88vh] flex flex-col"
                    >
                        {/* Drag handle */}
                        <div className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none">
                            <div
                                className="w-14 h-1.5 bg-ink/30 rounded-full"
                                data-testid="crew-hierarchy-drag-handle"
                            />
                        </div>

                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-ink bg-ink text-parchment">
                            <div className="leading-tight">
                                <div className="font-hand text-sm text-gold -mb-1">
                                    il codice del mare
                                </div>
                                <div className="font-display text-xl md:text-2xl tracking-tight">
                                    GERARCHIA DELLA CIURMA
                                </div>
                            </div>
                            <button
                                onClick={close}
                                aria-label="Chiudi gerarchia"
                                data-testid="crew-hierarchy-close"
                                className="w-10 h-10 border-2 border-parchment flex items-center justify-center hover:bg-bounty transition-manga shrink-0"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Progress recap */}
                        <div className="px-5 py-4 border-b-2 border-ink bg-white/60">
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-mono text-xs tracking-widest uppercase flex items-center gap-1">
                                    <TrendingUp size={14} /> il tuo rango
                                </div>
                                <div className="font-mono text-xs text-ink/60">
                                    {xp} XP · {formatBerry(berry)}
                                </div>
                            </div>
                            <div className="h-5 border-2 border-ink bg-white relative overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                    transition={{
                                        duration: 0.8,
                                        ease: "easeOut",
                                    }}
                                    className="h-full bg-bounty relative"
                                >
                                    <div className="absolute inset-0 halftone-red opacity-40" />
                                </motion.div>
                            </div>
                            <div className="font-hand text-sm text-ink/80 mt-2">
                                Sei{" "}
                                <span className="text-bounty font-bold">
                                    {level.badge} {level.name}
                                </span>
                                {nextLevel && (
                                    <>
                                        {" "}· mancano{" "}
                                        <span className="text-bounty font-bold">
                                            {nextLevel.threshold - xp} XP
                                        </span>{" "}
                                        per{" "}
                                        <span className="font-bold">
                                            {nextLevel.name}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Level ladder — scrollable */}
                        <div className="flex-1 overflow-y-auto p-5 space-y-2">
                            {LEVELS.map((l) => {
                                const isCurrent = l.level === level.level;
                                const unlocked = xp >= l.threshold;
                                return (
                                    <div
                                        key={l.level}
                                        data-testid={`crew-level-${l.level}`}
                                        className={`flex items-center gap-3 px-4 py-3 border-2 border-ink transition-manga ${
                                            isCurrent
                                                ? "bg-bounty text-parchment shadow-manga-lg"
                                                : unlocked
                                                  ? "bg-seafoam"
                                                  : "bg-white opacity-60"
                                        }`}
                                    >
                                        <span className="font-display text-xl w-8 text-center">
                                            {l.level}
                                        </span>
                                        <span className="text-2xl">
                                            {l.badge}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display tracking-wider text-base md:text-lg truncate">
                                                {l.name}
                                            </div>
                                            <div className="font-mono text-xs opacity-70">
                                                soglia · {l.threshold} XP
                                            </div>
                                        </div>
                                        {isCurrent && (
                                            <span
                                                className="font-hand text-sm bg-parchment text-ink px-2 py-0.5 border-2 border-parchment"
                                                data-testid="crew-hierarchy-current-badge"
                                            >
                                                sei qui
                                            </span>
                                        )}
                                    </div>
                                );
                            })}

                            <p className="text-xs font-mono text-ink/60 leading-relaxed pt-3">
                                Ogni XP guadagnato ti avvicina al prossimo
                                rango. Esplora figure, aggiungi al bottino,
                                iscriviti alla ciurma o completa un ordine per
                                scalare la gerarchia.
                            </p>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
